export type NotificationType = 'success' | 'error' | 'warning' | 'info';
export type ShowNotificationFn = (title: string, message: string, type: NotificationType) => void;

export interface Purchase {
  id: number;
  item_title: string;
  item_url?: string;
  item_image_url?: string;
  item_size?: string;
  item_brand?: string;
  item_condition?: string;
  price_paid: number;
  shipping_cost: number;
  protection_fee: number;
  total_paid: number;
  seller_name?: string;
  seller_url?: string;
  pickup_point_name?: string;
  pickup_point_address?: string;
  pickup_code?: string;
  pickup_deadline?: string;
  carrier?: string;
  tracking_number?: string;
  transaction_id?: string;
  status: 'paid' | 'shipped' | 'ready_for_pickup' | 'picked_up' | 'in_stock' | 'refunded';
  /** True si passage en stock depuis Remboursé (reste affiché onglet Remboursé) */
  inventory_after_refund?: boolean;
  notes?: string;
  created_at?: string;
  email_received_at?: string;
  stock_item_id?: number;
}

export interface StockItem {
  id: number;
  purchase_id?: number;
  item_title: string;
  item_size?: string;
  item_brand?: string;
  item_condition?: string;
  item_image_url?: string;
  purchase_price?: number;
  shipping_cost?: number;
  protection_fee?: number;
  target_sell_price?: number;
  status: 'waiting_pickup' | 'in_transit' | 'ready_for_pickup' | 'in_stock' | 'listed' | 'sold';
  is_received?: boolean;
  location?: string;
  notes?: string;
  added_at?: string;
  sale_id?: number;
  /** Présent en API si la ligne est liée à une vente CRM (onglet stock sortis) */
  linked_sale?: {
    id: number;
    status: string;
    item_title: string;
    sale_price: number;
  } | null;
}

export interface Sale {
  id: number;
  stock_item_id?: number;
  item_title: string;
  item_size?: string;
  item_brand?: string;
  sale_price: number;
  shipping_cost: number;
  vinted_commission: number;
  net_earnings?: number;
  purchase_price?: number;
  margin?: number;
  /** TVA % sur le prix de vente pour la banderole (sinon défaut paramètres CRM) */
  crm_tva_rate?: number;
  /** Emballage € par vente (sinon défaut paramètres CRM) */
  crm_packaging_cost?: number;
  buyer_name?: string;
  carrier?: string;
  tracking_number?: string;
  shipping_deadline?: string;
  shipped_at?: string;
  transaction_id?: string;
  item_image_url?: string;
  shipping_label_filename?: string;  // présent si un bordereau PDF est disponible
  status: 'sold' | 'packed' | 'shipped' | 'delivered' | 'completed';
  notes?: string;
  created_at?: string;
  email_received_at?: string;
  /** Email TX-COMPLETE sans vente CRM au même N° transaction : saisie chiffres débloquée */
  finalize_without_prior_sale?: boolean;
  package_weight_text?: string;
  is_manual?: boolean;
  invoice_party_vinted?: string;
  invoice_party_seller?: string;
  /** Ligne date telle que dans l’email TX-COMPLETE (ex. 05/04/2026 13 h 49) */
  invoice_transaction_datetime_text?: string;
  delivered_at?: string;
  completed_at?: string;
  /** True si la vente vient d'un email "ton article s'est vendu" et n'a pas encore de bordereau */
  from_sold_email?: boolean;
}

export interface CrmStats {
  year: number;
  totals: {
    ca: number;
    net_earnings: number;
    total_margin: number;
    total_purchases: number;
    sales_count: number;
    purchases_count: number;
  };
  monthly: {
    month: number;
    month_name: string;
    ca: number;
    net_earnings: number;
    margin: number;
    sales_count: number;
    purchases_total: number;
    purchases_count: number;
  }[];
  stock_by_status: Record<string, number>;
  urgent_sales: Sale[];
  urgent_pickups: Purchase[];
}

export const PURCHASE_STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  paid: { label: 'Payé', color: 'text-blue-700', bg: 'bg-blue-50' },
  shipped: { label: 'Expédié', color: 'text-indigo-700', bg: 'bg-indigo-50' },
  ready_for_pickup: { label: 'Prêt à retirer', color: 'text-amber-700', bg: 'bg-amber-50' },
  picked_up: { label: 'Récupéré', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  in_stock: { label: 'En stock', color: 'text-gray-700', bg: 'bg-gray-100' },
  refunded: { label: 'Remboursé', color: 'text-red-700', bg: 'bg-red-50' },
};

export const STOCK_STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  waiting_pickup: { label: 'En attente retrait', color: 'text-amber-700', bg: 'bg-amber-50' },
  in_transit: { label: 'En transit', color: 'text-blue-700', bg: 'bg-blue-50' },
  ready_for_pickup: { label: 'Prêt à retirer', color: 'text-violet-700', bg: 'bg-violet-50' },
  in_stock: { label: 'En stock', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  listed: { label: 'Mis en vente', color: 'text-indigo-700', bg: 'bg-indigo-50' },
  sold: { label: 'Vendu', color: 'text-gray-500', bg: 'bg-gray-100' },
};

export interface Parcel {
  id: number;
  carrier: 'mondial_relay' | 'vinted_go' | 'chronopost' | string;
  tracking_number?: string;
  item_title?: string;
  pickup_location_name?: string;
  pickup_address?: string;
  pickup_deadline?: string;
  pickup_code?: string;
  qr_code_url?: string;
  /** Lien suivi public (souvent Vinted Go en transit) */
  tracking_url?: string;
  /** Date de livraison estimée (email « arrive aujourd'hui ») */
  estimated_delivery_date?: string;
  /** Horaires du point relais (texte, Vinted Go) */
  opening_hours?: string;
  status: 'waiting' | 'in_transit' | 'picked_up';
  purchase_id?: number;
  raw_email_subject?: string;
  email_received_at?: string;
  created_at?: string;
}

/** Messagerie Vinted (achats / colis) */
export const VINTED_MESSAGING_URL = 'https://www.vinted.fr/inbox';

export const CARRIER_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  mondial_relay: { label: 'Mondial Relay', color: 'text-pink-700',   bg: 'bg-pink-50',   border: 'border-pink-200'   },
  vinted_go:     { label: 'Vinted Go',     color: 'text-teal-700',   bg: 'bg-teal-50',   border: 'border-teal-200'   },
  chronopost:    { label: 'Chronopost',    color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200'   },
  colissimo:     { label: 'Colissimo',     color: 'text-yellow-800', bg: 'bg-yellow-50', border: 'border-yellow-300' },
};

export interface OpexItem {
  id: number;
  name: string;
  monthly_amount: number;
  /** 'recurring' = mensuel récurrent | 'one_time' = montant unique pour une période */
  period_type?: 'recurring' | 'one_time';
  /** Clé de période ciblée pour one_time, ex: '2026-03', '2026-W14', '2026' */
  period_key?: string;
}

export interface AccountingPeriod {
  key: string;
  label: string;
  short_label: string;
  ca: number;
  purchases: number;
  net: number;
  count: number;
  /** Somme des montants TVA (€) par vente — même formule que la fiche vente */
  tva_amount?: number;
  /** Somme des frais d'emballage (€) saisis sur les fiches */
  packaging_total?: number;
}

export interface AccountingData {
  view: 'monthly' | 'annual' | 'weekly';
  year: number;
  periods: AccountingPeriod[];
  totals: {
    ca: number;
    purchases: number;
    net: number;
    count: number;
    tva_amount?: number;
    packaging_total?: number;
  };
  /** Toujours calculé pour l'année cible, indépendamment de la vue */
  year_totals: {
    ca: number;
    purchases: number;
    net: number;
    count: number;
    tva_amount?: number;
    packaging_total?: number;
  };
  opex_items: OpexItem[];
  stock_value: number;
  /** Aligné sur Paramètres CRM Ventes (même logique que la banderole revenu net) */
  fiscal_params?: { tva_rate: number; packaging_cost: number };
}

export const SALE_STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  sold:      { label: 'À confirmer',  color: 'text-amber-700',   bg: 'bg-amber-50'   },
  packed:    { label: 'À expédier',   color: 'text-blue-700',    bg: 'bg-blue-50'    },
  shipped:   { label: 'Expédié',      color: 'text-indigo-700',  bg: 'bg-indigo-50'  },
  delivered: { label: 'Livré',        color: 'text-emerald-700', bg: 'bg-emerald-50' },
  completed: { label: 'Finalisée',    color: 'text-gray-500',    bg: 'bg-gray-100'   },
};
